/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect, useState } from "react";
import { IntegratedSystem } from "../../api/integratedsystems";
import { DataProject } from "../../api/dataprojects";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Grid } from "@mui/material";
import { useSystemOrProject } from "../../components/integrated-systems/hooks";
import { Stats } from "../../components/mira/components/analytics/Stats";
import { Spacer } from "../../components/utils/Spacer";
import { StatusSplitChart } from "../../components/monitor/StatusSplitChart";
import { CompanyMoversPanel } from "../../components/monitor/CompanyMoversPanel";
import { JobTitleChangesPanel } from "../../components/monitor/JobTitleChangesPanel";
import {
  getCirromMonitorStatsForProjectOrSystem,
  ContactMonitorStats,
} from "../../api/cirrommonitor";
import { ContactMonitorDataOverview } from "../../components/monitor/DataOverview";
import { PageTitle } from "../../components/utils/PageTitle";

const MonitorStats: React.FC<{
  isDataProject: boolean;
  integratedSystemId: string;
}> = ({ isDataProject, integratedSystemId }) => {
  const [monitorStats, setMonitorStats] = useState<ContactMonitorStats | null>(
    null
  );

  useEffect(() => {
    if (integratedSystemId) {
      getCirromMonitorStatsForProjectOrSystem(
        !isDataProject ? Number.parseInt(integratedSystemId) : undefined,
        isDataProject ? Number.parseInt(integratedSystemId) : undefined
      ).then((response) => {
        setMonitorStats(response);
      });
    }
  }, [integratedSystemId, isDataProject]);

  const stats = [
    {
      title: "Unmatched Contacts",
      amount: monitorStats?.pending_contacts || 0,
      percentageText: "",
      percentageColor: "#00ff00",
      navigationUrl: isDataProject
        ? `/data-project/contact-monitor/linking/${integratedSystemId}`
        : `/product/contact-monitor/linking/${integratedSystemId}`,
    },
    {
      title: "Company Movers",
      amount: monitorStats?.company || 0,
      percentageText: "",
      percentageColor: "#00ff00",
      navigationUrl: isDataProject
        ? `/data-project/contact-monitor/company/${integratedSystemId}?tab=job_movers`
        : `/product/contact-monitor/company/${integratedSystemId}?tab=job_movers`,
    },
    {
      title: "Job Title Changes",
      amount: monitorStats?.job_title || 0,
      percentageText: "",
      percentageColor: "#00ff00",
      navigationUrl: isDataProject
        ? `/data-project/contact-monitor/company/${integratedSystemId}?tab=job_title`
        : `/product/contact-monitor/company/${integratedSystemId}?tab=job_title`,
    },
    {
      title: "Name",
      amount: monitorStats?.data_enrichments || 0,
      percentageText: "",
      percentageColor: "#00ff00",
      navigationUrl: isDataProject
        ? `/data-project/contact-monitor/name/${integratedSystemId}`
        : `/product/contact-monitor/name/${integratedSystemId}`,
    },
    {
      title: "Location",
      amount: monitorStats?.location || 0,
      percentageText: "",
      percentageColor: "#00ff00",
      navigationUrl: isDataProject
        ? `/data-project/contact-monitor/location/${integratedSystemId}`
        : `/product/contact-monitor/location/${integratedSystemId}`,
    },
  ];

  return (
    <>
      <Spacer />
      <Grid container spacing={6}>
        {stats.map((stat) => (
          <Grid
            key={stat.title}
            size={{
              xs: 12,
              sm: 12,
              md: 2.4,
            }}
          >
            <Stats
              title={stat.title}
              amount={stat.amount}
              percentageText={stat.percentageText}
              percentageColor={stat.percentageColor}
              clickPath={stat.navigationUrl}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export const ContactMonitorDashboard = React.memo(
  (props: {
    userSystems: IntegratedSystem[] | DataProject[];
    isDataProject: boolean;
    setSelectedSystemId: (number) => void;
  }) => {
    const { integratedSystemId } = useParams();

    useEffect(() => {
      if (integratedSystemId) {
        if (props.isDataProject) {
          const dataProject = (props.userSystems as DataProject[]).find(
            (p) => p.id === Number.parseInt(integratedSystemId)
          );
          if (dataProject) {
            props.setSelectedSystemId(dataProject.integrated_system_id);
          }
        } else {
          props.setSelectedSystemId(Number.parseInt(integratedSystemId));
        }
      }
    }, [integratedSystemId, props.isDataProject, props.userSystems]);

    const systemOrProject = useSystemOrProject(
      props.userSystems,
      integratedSystemId
    );

    if (!systemOrProject) {
      return (
        <>
          <Helmet title="Cirrom Monitor" />
        </>
      );
    }

    return (
      <PageTitle
        pageTitle="Monitor Dashboard | CIRROM"
        pageHeader={"Monitor Data"}
        isDataProject={props.isDataProject}
        integratedSystemId={systemOrProject?.id}
        systemOrProjectHeader={systemOrProject?.name}
        isMonitorPage
      >
        <MonitorStats
          isDataProject={props.isDataProject}
          integratedSystemId={integratedSystemId}
        />
        <Spacer />
        <Grid container spacing={6}>
          <Grid
            size={{
              xs: 12,
              lg: 6,
            }}
          >
            <CompanyMoversPanel
              isDataProject={props.isDataProject}
              dataProjectId={systemOrProject.id}
              integratedSystemId={
                props.isDataProject
                  ? systemOrProject.integrated_system_id
                  : systemOrProject.id
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 6,
            }}
          >
            <StatusSplitChart
              isDataProject={props.isDataProject}
              integratedSystemId={integratedSystemId}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 6,
            }}
          >
            <JobTitleChangesPanel
              isDataProject={props.isDataProject}
              dataProjectId={systemOrProject.id}
              integratedSystemId={
                props.isDataProject
                  ? systemOrProject.integrated_system_id
                  : systemOrProject.id
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 6,
            }}
          >
            <ContactMonitorDataOverview
              isDataProject={props.isDataProject}
              dataProjectId={systemOrProject.id}
              integratedSystemId={
                props.isDataProject
                  ? systemOrProject.integrated_system_id
                  : systemOrProject.id
              }
            />
          </Grid>
        </Grid>
      </PageTitle>
    );
  }
);
